import { mainUploadMediaToS3 } from '../../Api/mediaS3Api'

const getDimensions = async (url) => {
  return new Promise((resolve) => {
    const img = new Image()
    const dimensions = {}
    img.onload = function () {
      dimensions.width = this.width
      dimensions.height = this.height
      resolve(dimensions)
    }
    img.src = url
  })
}

const getVideoDimensions = (url) => {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    video.addEventListener(
      'loadedmetadata',
      function () {
        const height = this.videoHeight
        const width = this.videoWidth
        resolve({ height, width })
      },
      false
    )
    video.src = url
  })
}

const scaleDimensions = (width, height, maxWidth, maxHeight) => {
  const ratio = Math.min(maxWidth / width, maxHeight / height, 1)
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  }
}

const generateImageThumbnail = async (imageBlob, originalDimensions) => {
  const url = URL.createObjectURL(imageBlob)
  const img = new Image()
  img.src = url
  await new Promise((resolve) => {
    img.onload = resolve
  })

  const { width: targetWidth, height: targetHeight } = scaleDimensions(
    originalDimensions.width,
    originalDimensions.height,
    1080,
    1920
  )

  const canvas = document.createElement('canvas')
  canvas.width = targetWidth
  canvas.height = targetHeight
  const ctx = canvas.getContext('2d')
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight)

  const mimeType = imageBlob.type?.includes('image') ? imageBlob.type : 'image/jpeg'

  const thumbnailBlob = await new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(blob)
      },
      mimeType,
      0.9
    )
  })

  URL.revokeObjectURL(url)
  return {
    blob: thumbnailBlob,
    dimensions: { width: targetWidth, height: targetHeight },
  }
}

const generateVideoThumbnail = async (videoBlob, originalDimensions) => {
  const url = URL.createObjectURL(videoBlob)
  const video = document.createElement('video')
  video.src = url
  video.muted = true
  video.playsInline = true

  await new Promise((resolve, reject) => {
    video.onloadeddata = resolve
    video.onerror = reject
  })

  // Make sure the very first decoded frame is available before drawing it to the canvas.
  const waitForFirstFrame = async () => {
    if (video.requestVideoFrameCallback) {
      return new Promise((resolve) => video.requestVideoFrameCallback(() => resolve()))
    }

    return new Promise((resolve) => {
      const onFrame = () => {
        clearTimeout(fallbackTimer)
        video.removeEventListener('seeked', onFrame)
        video.removeEventListener('timeupdate', onFrame)
        resolve()
      }
      const fallbackTimer = setTimeout(onFrame, 500)
      video.addEventListener('seeked', onFrame, { once: true })
      video.addEventListener('timeupdate', onFrame, { once: true })
      try {
        video.currentTime = 0
      } catch (e) {
        onFrame()
      }
    })
  }

  await waitForFirstFrame()

  const { width: targetWidth, height: targetHeight } = scaleDimensions(
    originalDimensions.width,
    originalDimensions.height,
    1080,
    1920
  )

  const canvas = document.createElement('canvas')
  canvas.width = targetWidth
  canvas.height = targetHeight
  const ctx = canvas.getContext('2d')
  ctx.drawImage(video, 0, 0, targetWidth, targetHeight)

  const thumbnailBlob = await new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9)
  })

  URL.revokeObjectURL(url)
  return {
    blob: thumbnailBlob,
    dimensions: { width: targetWidth, height: targetHeight },
  }
}

const generateVideoPreview = async (videoBlob, originalDimensions) => {
  if (typeof MediaRecorder === 'undefined') {
    return null
  }

  const url = URL.createObjectURL(videoBlob)
  const video = document.createElement('video')
  video.src = url
  video.muted = true
  video.playsInline = true

  const { width: targetWidth, height: targetHeight } = scaleDimensions(
    originalDimensions.width,
    originalDimensions.height,
    1080,
    1920
  )

  await new Promise((resolve) => {
    video.onloadedmetadata = resolve
  })

  const canvas = document.createElement('canvas')
  canvas.width = targetWidth
  canvas.height = targetHeight

  const ctx = canvas.getContext('2d')
  const stream = canvas.captureStream()
  let recorder

  try {
    recorder = new MediaRecorder(stream, {
      mimeType: 'video/webm;codecs=vp9,opus',
    })
  } catch (e) {
    URL.revokeObjectURL(url)
    return null
  }

  const chunks = []
  recorder.ondataavailable = (event) => {
    if (event.data && event.data.size > 0) {
      chunks.push(event.data)
    }
  }

  const drawFrame = () => {
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    if (!video.paused && !video.ended) {
      requestAnimationFrame(drawFrame)
    }
  }

  const recordingPromise = new Promise((resolve) => {
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: recorder.mimeType || 'video/webm' })
      resolve(blob)
    }
  })

  await video.play().catch(() => {})
  recorder.start()
  requestAnimationFrame(drawFrame)

  const maxDurationSeconds = 5
  setTimeout(
    () => {
      if (recorder.state !== 'inactive') {
        recorder.stop()
      }
      video.pause()
    },
    Math.min(video.duration || maxDurationSeconds, maxDurationSeconds) * 1000
  )

  const previewBlob = await recordingPromise
  URL.revokeObjectURL(url)

  return {
    blob: previewBlob,
    dimensions: {
      width: canvas.width,
      height: canvas.height,
    },
  }
}

export const sendImage = async (imgUrl, createMediaPresignedUrlRequest, getMediaRequest, userId = null) => {
  const mediaData = await fetch(imgUrl).then((res) => res.blob())
  let dimensions
  let previewData = null
  let thumbnailData = null

  if (mediaData.type.includes('video')) {
    dimensions = await getVideoDimensions(imgUrl)
    previewData = await generateVideoPreview(mediaData, dimensions)
    thumbnailData = await generateVideoThumbnail(mediaData, dimensions)
  } else if (mediaData.type.includes('image')) {
    dimensions = await getDimensions(imgUrl)
    thumbnailData = await generateImageThumbnail(mediaData, dimensions)
  }

  //Create a presigned url to upload media
  const mediaPresignedUrlRequestParameters = [mediaData.type, `${dimensions?.width},${dimensions?.height}`]
  if (userId) {
    mediaPresignedUrlRequestParameters.push(userId)
  }

  const createPresignedUrlResp = await createMediaPresignedUrlRequest(...mediaPresignedUrlRequestParameters)

  // check http code
  if (createPresignedUrlResp.status < 200 || createPresignedUrlResp.status >= 300) {
    console.debug('could not create presigned url, http status: ', createPresignedUrlResp.status)
  }
  console.debug(`[createOfferSlice.js] create presigned url response: `, createPresignedUrlResp)
  // Upload media to S3 using the presigned url and headers
  const mediaId = createPresignedUrlResp.data['mediaId']
  const uploadToS3Resp = await mainUploadMediaToS3(
    createPresignedUrlResp.data['presignedUploadUrl'],
    createPresignedUrlResp.data['headers'],
    mediaData
  )
  console.debug(`[createOfferSlice.js] uploaded media to s3, response: `, uploadToS3Resp)
  // check http code
  if (uploadToS3Resp.status < 200 || uploadToS3Resp.status >= 300) {
    console.debug('could not upload media to s3, http status: ', uploadToS3Resp.status)
  }

  const uploadAdditionalMedia = async (blob, dims) => {
    if (!blob) return null
    const presigned = await createMediaPresignedUrlRequest(
      blob.type,
      `${dims.width},${dims.height}`,
      userId ?? undefined
    )
    if (!presigned || presigned.status < 200 || presigned.status >= 300) return null

    const uploadResp = await mainUploadMediaToS3(presigned.data['presignedUploadUrl'], presigned.data['headers'], blob)
    if (!uploadResp || uploadResp.status < 200 || uploadResp.status >= 300) return null

    const delay = (s) => new Promise((res) => setTimeout(res, s * 1000))
    const maxRetries = 5
    const secondsBetweenRetries = 3
    let attempts = 0
    while (attempts <= maxRetries) {
      await delay(secondsBetweenRetries)
      const mediaResp = await getMediaRequest(presigned.data['mediaId'])
      if (mediaResp?.status === 200) {
        return mediaResp.data.id
      }
      attempts++
    }
    return null
  }

  const previewMediaId = previewData?.blob
    ? await uploadAdditionalMedia(previewData.blob, previewData.dimensions)
    : null

  const thumbnailMediaId = thumbnailData?.blob
    ? await uploadAdditionalMedia(thumbnailData.blob, thumbnailData.dimensions)
    : null

  // Wait for media to be added to the DB by media lambda
  // if we try to create the offer before the media record is added, the backend will return a 'media not found' error
  const delay = (s) => new Promise((res) => setTimeout(res, s * 1000))
  const maxRetries = 8
  const secondsBetweenRetries = 5
  let requestsSentCount = 0
  let mediaUrl = null
  let getMediaResp
  while (true) {
    // wait
    console.debug('waiting for media...')
    await delay(secondsBetweenRetries)
    // check for media
    getMediaResp = await getMediaRequest(mediaId)
    if (getMediaResp.status === 200) {
      mediaUrl = getMediaResp.data.url
      console.debug('media found')
      // stop checking if media found
      break
    }
    // check counter
    requestsSentCount++
    if (requestsSentCount > maxRetries) {
      console.log(`media not found after ${maxRetries * secondsBetweenRetries} seconds`)
      return null
    }
  }
  getMediaResp.data.mediaId = mediaId
  getMediaResp.data.mediaUrl = mediaUrl
  getMediaResp.data.videoPreviewMediaId = previewMediaId
  getMediaResp.data.thumbnailMediaId = thumbnailMediaId
  return getMediaResp.data
}
